import {
  deleteInvoice,
  getInvoicesHistoryList,
  getInvoicesList,
  getPendingInvoicesList,
  resetInvoiceIDs,
  setBill,
  setBillChargeTypes,
  setBillChargeTypesSelection,
  setBills,
  setBillsSelection,
  setInvoices,
  setPrePayments,
  setPrePaymentSelection,
  setPrePaymentsForPODetail,
  setSaleInvoice,
  setSaleInvoices,
  setSaleInvoiceSelection,
  setCustomerCreditNote,
  setCustomerCreditNotes,
  setCustomerCreditNotesSelection,
  setSupplierCreditNote,
  setSupplierCreditNotes,
  setSupplierCreditNotesSelection,
  updateBillStatus,
  updateStatusForBill,
} from "../slice/invoiceSlice";
import { setNotification } from "../slice/miscSlice";
import { apiSlice } from "./apiSlice";
import { fileResponseHandler } from "./utils";
import {
  prepaymentByPurchaseOrderTagsFromParams,
  prepaymentPurchaseOrderInvalidationTags,
} from "./prepaymentCacheInvalidation";

export const invoiceApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    createGeneralBill: builder.mutation({
      query: data => ({
        url: `/api_invoice/bill/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Bill"],
    }),
    createInvoice: builder.mutation({
      query: data => ({
        url: `/api_docdigit/document/create_invoice/`,
        method: "POST",
        body: data,
      }),
    }),
    createPrePayment: builder.mutation({
      query: data => ({
        url: `/api_invoice/pre_payment/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: prepaymentPurchaseOrderInvalidationTags,
    }),
    updatePrePayment: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_invoice/pre_payment/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: prepaymentPurchaseOrderInvalidationTags,
    }),
    voidPrePayments: builder.mutation({
      query: ({ prepayments: _prepayments, ...body }) => ({
        url: `/api_invoice/pre_payment/bulk_void/`,
        method: "POST",
        body,
      }),
      invalidatesTags: prepaymentPurchaseOrderInvalidationTags,
    }),
    updatePrePaymentStatus: builder.mutation({
      query: ({ prepayments: _prepayments, ...body }) => ({
        url: `/api_invoice/pre_payment/bulk_update_status/`,
        method: "POST",
        body,
      }),
      invalidatesTags: prepaymentPurchaseOrderInvalidationTags,
    }),
    createSupplierCreditNote: builder.mutation({
      query: data => ({
        url: `/api_invoice/supplier_credit_note/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["SupplierCreditNote", "Bill"],
    }),
    updateSupplierCreditNote: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_invoice/supplier_credit_note/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "SupplierCreditNote", id },
        "SupplierCreditNote",
        "Bill",
      ],
    }),
    updateSupplierCreditNoteStatus: builder.mutation({
      query: ({ id, statusId }) => ({
        url: `/api_invoice/supplier_credit_note/${id}/`,
        method: "PATCH",
        body: { status_id: statusId },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "SupplierCreditNote", id },
        "SupplierCreditNote",
        "Bill",
      ],
    }),
    submitSupplierCreditNoteToXero: builder.mutation({
      query: ({ id }) => ({
        url: `/api_invoice/supplier_credit_note/${id}/submit_to_xero/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "SupplierCreditNote", id },
        "SupplierCreditNote",
        "Bill",
      ],
    }),
    createCustomerCreditNote: builder.mutation({
      query: data => ({
        url: `/api/invoice/v1/customer_credit_note/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["CustomerCreditNote", "SaleInvoice"],
    }),
    updateCustomerCreditNote: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/invoice/v1/customer_credit_note/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "CustomerCreditNote", id },
        "CustomerCreditNote",
        "SaleInvoice",
      ],
    }),
    submitCustomerCreditNote: builder.mutation({
      query: ({ id }) => ({
        url: `/api/invoice/v1/customer_credit_note/${id}/submit/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "CustomerCreditNote", id },
        "CustomerCreditNote",
        "SaleInvoice",
      ],
    }),
    approveCustomerCreditNote: builder.mutation({
      query: ({ id }) => ({
        url: `/api/invoice/v1/customer_credit_note/${id}/approve/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "CustomerCreditNote", id },
        "CustomerCreditNote",
        "SaleInvoice",
      ],
    }),
    voidCustomerCreditNote: builder.mutation({
      query: ({ id }) => ({
        url: `/api/invoice/v1/customer_credit_note/${id}/void/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "CustomerCreditNote", id },
        "CustomerCreditNote",
        "SaleInvoice",
      ],
    }),
    markCustomerCreditNotePaid: builder.mutation({
      query: ({ id }) => ({
        url: `/api/invoice/v1/customer_credit_note/${id}/mark_paid/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "CustomerCreditNote", id },
        "CustomerCreditNote",
        "SaleInvoice",
      ],
    }),
    submitCustomerCreditNoteToXero: builder.mutation({
      query: ({ id }) => ({
        url: `/api/invoice/v1/customer_credit_note/${id}/submit_to_xero/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "CustomerCreditNote", id },
        "CustomerCreditNote",
        "SaleInvoice",
      ],
    }),
    createShipment: builder.mutation({
      query: data => ({
        url: `/api_docdigit/document/create_shipment/`,
        method: "POST",
        body: data,
      }),
    }),

    getBill: builder.query({
      query: id => `/api_invoice/bill/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "Bill", id }],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setBill(result.data))).catch(error => {});
      },
    }),
    getBills: builder.query({
      query: (params = null) => (params ? `/api_invoice/bill/?${params}` : "/api_invoice/bill/"),
      providesTags: ["Bill"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setBills(result.data))).catch(error => {});
      },
    }),
    getBillsSelection: builder.query({
      query: () => `/api_invoice/bill/selection`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setBillsSelection(result.data))).catch(error => {});
      },
    }),
    getSaleInvoice: builder.query({
      query: id => `/api/invoice/v1/sale_invoice/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "SaleInvoice", id }],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setSaleInvoice(result.data))).catch(error => {});
      },
    }),
    getSaleInvoiceIssues: builder.query({
      query: id => `/api/invoice/v1/sale_invoice/${id}/issues/`,
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, id) => [{ type: "SaleInvoice", id: `issues-${id}` }],
      transformResponse: response => response?.data ?? response,
    }),
    getSaleInvoiceLineIssues: builder.query({
      query: id => `/api/invoice/v1/sale_invoice_line/${id}/issues/`,
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, id) => [
        { type: "SaleInvoice", id: `line-issues-${id}` },
      ],
      transformResponse: response => response?.data ?? response,
    }),
    getSaleInvoices: builder.query({
      query: (params = null) =>
        params ? `/api/invoice/v1/sale_invoice/?${params}` : "/api/invoice/v1/sale_invoice/",
      providesTags: ["SaleInvoice"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setSaleInvoices(result.data))).catch(error => {});
      },
    }),
    getSaleInvoicesSelection: builder.query({
      query: () => `/api/invoice/v1/sale_invoice/selection/`,
      providesTags: ["SaleInvoice"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setSaleInvoiceSelection(result.data)))
          .catch(error => {});
      },
    }),
    createSaleInvoice: builder.mutation({
      query: data => ({
        url: `/api/invoice/v1/sale_invoice/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["SaleInvoice"],
    }),
    updateSaleInvoice: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/invoice/v1/sale_invoice/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["SaleInvoice"],
    }),
    updateSaleInvoicePaymentAdvice: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/invoice/v1/sale_invoice/${id}/payment-advice/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["SaleInvoice"],
    }),
    deleteSaleInvoice: builder.mutation({
      query: ({ id }) => ({
        url: `/api/invoice/v1/sale_invoice/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["SaleInvoice"],
    }),
    markSaleInvoiceAsPaid: builder.mutation({
      query: ({ id }) => ({
        url: `/api/invoice/v1/sale_invoice/${id}/mark-as-paid/`,
        method: "POST",
      }),
      invalidatesTags: ["SaleInvoice"],
    }),
    createCustomerPayment: builder.mutation({
      query: body => ({
        url: `/api/invoice/v1/customer_payment/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["SaleInvoice", "CustomerPayment"],
    }),
    getCustomerPayments: builder.query({
      query: (params = null) =>
        params
          ? `/api/invoice/v1/customer_payment/?${params}`
          : `/api/invoice/v1/customer_payment/`,
      providesTags: ["CustomerPayment"],
    }),
    sendSaleInvoice: builder.mutation({
      query: ({ id }) => ({
        url: `/api/invoice/v1/sale_invoice/${id}/send/`,
        method: "POST",
      }),
      invalidatesTags: ["SaleInvoice"],
    }),
    submitSaleInvoice: builder.mutation({
      query: ({ id }) => ({
        url: `/api/invoice/v1/sale_invoice/${id}/submit/`,
        method: "POST",
      }),
      invalidatesTags: ["SaleInvoice"],
    }),
    approveSaleInvoice: builder.mutation({
      query: ({ id }) => ({
        url: `/api/invoice/v1/sale_invoice/${id}/approve/`,
        method: "POST",
      }),
      invalidatesTags: ["SaleInvoice"],
    }),
    processSaleInvoiceFulfillment: builder.mutation({
      query: ({ invoice_ids = [], invoice_line_ids = [] }) => ({
        url: `/api/invoice/v1/sale_invoice/process_fulfillment/`,
        method: "POST",
        body: { invoice_ids, invoice_line_ids },
      }),
      invalidatesTags: ["SaleInvoice"],
    }),
    downloadSaleInvoicePdf: builder.query({
      query: arg => {
        const id = arg && typeof arg === "object" ? arg.id : arg;
        const layoutId = arg && typeof arg === "object" ? arg.layoutId : null;
        const suffix = layoutId ? `?layout_id=${encodeURIComponent(layoutId)}` : "";
        return {
          url: `/api/invoice/v1/sale_invoice/${id}/download_pdf/${suffix}`,
          responseHandler: fileResponseHandler,
        };
      },
    }),
    downloadSaleInvoiceExcel: builder.query({
      query: arg => {
        const id = arg && typeof arg === "object" ? arg.id : arg;
        const layoutId = arg && typeof arg === "object" ? arg.layoutId : null;
        const suffix = layoutId ? `?layout_id=${encodeURIComponent(layoutId)}` : "";
        return {
          url: `/api/invoice/v1/sale_invoice/${id}/download_excel/${suffix}`,
          responseHandler: fileResponseHandler,
        };
      },
    }),
    shareSaleInvoice: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/invoice/v1/sale_invoice/${id}/share/`,
        method: "POST",
        body: data,
      }),
    }),
    voidSaleInvoice: builder.mutation({
      query: ({ id }) => ({
        url: `/api/invoice/v1/sale_invoice/${id}/void/`,
        method: "POST",
      }),
      invalidatesTags: ["SaleInvoice"],
    }),
    writeOffSaleInvoice: builder.mutation({
      query: ({ id }) => ({
        url: `/api/invoice/v1/sale_invoice/${id}/write-off/`,
        method: "POST",
      }),
      invalidatesTags: ["SaleInvoice"],
    }),
    getSaleInvoiceLines: builder.query({
      query: (params = null) =>
        params
          ? `/api/invoice/v1/sale_invoice_line/?${params}`
          : "/api/invoice/v1/sale_invoice_line/",
      providesTags: ["SaleInvoice"],
    }),
    getSaleInvoiceCharges: builder.query({
      query: (params = null) =>
        params
          ? `/api/invoice/v1/sale_invoice_charges/?${params}`
          : "/api/invoice/v1/sale_invoice_charges/",
    }),
    getBillChargeAmountTypesSelection: builder.query({
      query: () => `/api_invoice/bill_charge_amount_type/`,
    }),
    getBillChargeDistributionTypesSelection: builder.query({
      query: () => `/api_invoice/bill_charge_distribution_type/`,
    }),
    getBillChargeTypesSelection: builder.query({
      query: () => "/api_invoice/bill_charge_type/selection/",
      providesTags: ["BillChargeType"],
      transformResponse: response =>
        Array.isArray(response)
          ? response
          : Array.isArray(response?.results)
            ? response.results
            : [],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setBillChargeTypesSelection(result.data || [])))
          .catch(() => {
            dispatch(setBillChargeTypesSelection([]));
          });
      },
    }),
    getBillChargeTypes: builder.query({
      query: (params = null) =>
        params ? `/api_invoice/bill_charge_type/?${params}` : "/api_invoice/bill_charge_type/",
      providesTags: ["BillChargeType"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setBillChargeTypes(result.data))).catch(error => {});
      },
    }),
    getBillChargeTypeById: builder.query({
      query: id => `/api_invoice/bill_charge_type/${id}/`,
      providesTags: ["BillChargeType"],
    }),
    createBillChargeType: builder.mutation({
      query: data => ({
        url: `/api_invoice/bill_charge_type/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["BillChargeType"],
    }),
    updateBillChargeType: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api_invoice/bill_charge_type/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["BillChargeType"],
    }),
    deleteBillChargeType: builder.mutation({
      query: id => ({
        url: `/api_invoice/bill_charge_type/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["BillChargeType"],
    }),
    getInvoices: builder.query({
      query: (params = null) =>
        params ? `/api_invoice/invoice/?${params}` : "/api_invoice/invoice/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setInvoices(result.data))).catch(error => {});
      },
    }),
    getInvoicesHistoryList: builder.query({
      query: (params = null) =>
        params ? `/api_invoice/invoice_history/?${params}` : "/api_invoice/invoice_history/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getInvoicesHistoryList(result.data)))
          .catch(error => {});
      },
    }),
    getInvoicesList: builder.query({
      query: () => "/api_invoice/invoice/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getInvoicesList(result.data))).catch(error => {});
      },
    }),
    getPendingInvoicesList: builder.query({
      query: (params = null) =>
        params ? `/api_invoice/pending_invoice/?${params}` : "/api_invoice/pending_invoice/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPendingInvoicesList(result.data)))
          .catch(error => {});
      },
    }),
    getSupplierCreditNote: builder.query({
      query: id => `/api_invoice/supplier_credit_note/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "SupplierCreditNote", id }],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setSupplierCreditNote(result.data)))
          .catch(error => {});
      },
    }),
    getCustomerCreditNote: builder.query({
      query: id => `/api/invoice/v1/customer_credit_note/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "CustomerCreditNote", id }],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setCustomerCreditNote(result.data)))
          .catch(() => {});
      },
    }),
    getCustomerCreditNotes: builder.query({
      query: (params = null) =>
        params
          ? `/api/invoice/v1/customer_credit_note/?${params}`
          : "/api/invoice/v1/customer_credit_note/",
      providesTags: ["CustomerCreditNote"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setCustomerCreditNotes(result.data)))
          .catch(() => {});
      },
    }),
    getCustomerCreditNotesSelection: builder.query({
      query: () => "/api/invoice/v1/customer_credit_note/selection/",
      providesTags: ["CustomerCreditNote"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setCustomerCreditNotesSelection(result.data)))
          .catch(() => {});
      },
    }),
    getPrePayments: builder.query({
      query: (params = null) =>
        params ? `/api_invoice/pre_payment/?${params}` : "/api_invoice/pre_payment/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setPrePayments(result.data))).catch(error => {});
      },
      providesTags: ["PrePayment"],
    }),
    getPrePaymentsByPOId: builder.query({
      query: (params = null) =>
        params ? `/api_invoice/pre_payment/?${params}` : "/api_invoice/pre_payment/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setPrePaymentsForPODetail(result.data)))
          .catch(error => {});
      },
      providesTags: (_result, _error, params) => prepaymentByPurchaseOrderTagsFromParams(params),
    }),
    getPrePaymentById: builder.query({
      query: id => `/api_invoice/pre_payment/${id}/`,
      providesTags: ["PrePayment"],
    }),
    getPrePaymentSelection: builder.query({
      query: () => `/api_invoice/pre_payment/selection/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setPrePaymentSelection(result.data)))
          .catch(error => {});
      },
    }),
    getSupplierCreditNotesSelection: builder.query({
      query: () => "/api_invoice/supplier_credit_note/selection/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setSupplierCreditNotesSelection(result.data)))
          .catch(error => {});
      },
    }),
    getSupplierCreditNotes: builder.query({
      query: (params = null) =>
        params
          ? `/api_invoice/supplier_credit_note/?${params}`
          : "/api_invoice/supplier_credit_note/",
      providesTags: ["SupplierCreditNote"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setSupplierCreditNotes(result.data)))
          .catch(error => {});
      },
    }),
    publishAllInvoices: builder.mutation({
      query: ({ data }) => ({
        url: `/api_invoice/invoice/publish_invoices/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            let failIDs = result.data.filter(item => item.result === false).map(item => item.id);
            const message = `These invoice no. [${failIDs}] are fail to create bill.`;
            dispatch(
              setNotification({ type: "error", message: message || "Something went wrong" }),
            );
            dispatch(resetInvoiceIDs());
          })
          .catch(error => {});
      },
    }),
    publishAllInvoicesManual: builder.mutation({
      query: ({ data }) => ({
        url: `/api_invoice/invoice/publish_invoices_manually/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(resetInvoiceIDs())).catch(error => {});
      },
    }),
    removeInvoice: builder.mutation({
      query: ({ invoiceId }) => ({
        url: `/api_invoice/invoice/${invoiceId}/`,
        method: "DELETE",
        data: {},
      }),
      onQueryStarted: ({ invoiceId }, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(_ => dispatch(deleteInvoice(invoiceId))).catch(error => {});
      },
    }),

    /**
     * Save header, lines, and charges on an idle Draft bill.
     *
     * @param {Object} args
     * @param {number|string} args.id - The bill being edited.
     */
    updateBill: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_invoice/bill/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Bill", id }, "Bill"],
    }),
    /**
     * Submit a bill to an external system or internally.
     *
     * [BUSINESS RULE] If an external integration is present, this triggers an async
     * task and the bill remains in DRAFT until confirmed. Otherwise, it immediately
     * transitions to SUBMIT internally.
     *
     * @param {Object} args
     * @param {number|string} args.id - The ID of the bill to submit.
     */
    submitBill: builder.mutation({
      query: ({ id }) => ({
        url: `/api_invoice/bill/${id}/submit/`,
        method: "PUT",
        body: {},
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Bill", id }, "Bill"],
      onQueryStarted: ({ id, fromDetailPage }, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            if (fromDetailPage) {
              dispatch(updateStatusForBill(result.data));
            } else {
              dispatch(updateBillStatus({ id, ...result.data }));
            }
          })
          .catch(error => {});
      },
    }),
    /**
     * Update the status of a bill (e.g., from SUBMIT to APPROVED or VOIDED).
     *
     * [BUSINESS RULE] Manual status updates via this endpoint are blocked by the backend
     * if the bill is managed by an external financial system (e.g., Xero).
     *
     * @param {Object} args
     * @param {number|string} args.id - The ID of the bill to update.
     * @param {string} [args.status] - The target status code.
     */
    updateBillStatus: builder.mutation({
      query: ({ id, fromDetailPage: _fromDetailPage, ...rest }) => ({
        url: `/api_invoice/bill/${id}/update_status/`,
        method: "PATCH",
        body: rest,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Bill", id }, "Bill"],
      onQueryStarted: ({ id, fromDetailPage }, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            if (fromDetailPage) {
              dispatch(updateStatusForBill(result.data));
            } else {
              dispatch(updateBillStatus({ id, ...result.data }));
            }
          })
          .catch(() => {});
      },
    }),

    getAllocateCostSources: builder.query({
      query: ({ params }) => {
        const urlMap = {
          purchase_order: "/api_po/purchase_order/?mode=selection",
          consignment: "/api_shipment/consignment/?type_ids=purchase",
          receipt: "/api_po/receipt/?document_type_ids=Purchase Receipt",
        };
        const url = urlMap[params] || "";
        if (!url) throw new Error("Invalid allocate cost source");
        return url;
      },
    }),
  }),
});

export const {
  useCreateGeneralBillMutation,
  useCreateInvoiceMutation,
  useCreateShipmentMutation,
  useApproveCustomerCreditNoteMutation,
  useCreateCustomerCreditNoteMutation,
  useCreateSupplierCreditNoteMutation,
  useGetCustomerCreditNoteQuery,
  useGetCustomerCreditNotesQuery,
  useGetCustomerCreditNotesSelectionQuery,
  useLazyGetCustomerCreditNotesSelectionQuery,
  useMarkCustomerCreditNotePaidMutation,
  useSubmitCustomerCreditNoteMutation,
  useSubmitCustomerCreditNoteToXeroMutation,
  useUpdateCustomerCreditNoteMutation,
  useVoidCustomerCreditNoteMutation,
  useCreatePrePaymentMutation,
  useUpdatePrePaymentMutation,
  useVoidPrePaymentsMutation,
  useUpdatePrePaymentStatusMutation,
  useGetAllocateCostSourcesQuery,
  useGetBillChargeAmountTypesSelectionQuery,
  useGetBillChargeDistributionTypesSelectionQuery,
  useGetBillChargeTypesSelectionQuery,
  useLazyGetBillChargeTypesSelectionQuery,
  useGetBillChargeTypesQuery,
  useGetBillChargeTypeByIdQuery,
  useCreateBillChargeTypeMutation,
  useUpdateBillChargeTypeMutation,
  useDeleteBillChargeTypeMutation,
  useGetBillQuery,
  useGetBillsQuery,
  useLazyGetBillsSelectionQuery,
  useGetInvoicesHistoryListQuery,
  useGetInvoicesListQuery,
  useGetInvoicesQuery,
  useGetSaleInvoiceQuery,
  useGetSaleInvoiceIssuesQuery,
  useGetSaleInvoiceLineIssuesQuery,
  useGetSaleInvoicesQuery,
  useGetSaleInvoicesSelectionQuery,
  useLazyGetSaleInvoicesSelectionQuery,
  useCreateSaleInvoiceMutation,
  useUpdateSaleInvoiceMutation,
  useUpdateSaleInvoicePaymentAdviceMutation,
  useDeleteSaleInvoiceMutation,
  useMarkSaleInvoiceAsPaidMutation,
  useCreateCustomerPaymentMutation,
  useGetCustomerPaymentsQuery,
  useSendSaleInvoiceMutation,
  useSubmitSaleInvoiceMutation,
  useApproveSaleInvoiceMutation,
  useProcessSaleInvoiceFulfillmentMutation,
  useLazyDownloadSaleInvoicePdfQuery,
  useLazyDownloadSaleInvoiceExcelQuery,
  useShareSaleInvoiceMutation,
  useVoidSaleInvoiceMutation,
  useWriteOffSaleInvoiceMutation,
  useGetSaleInvoiceLinesQuery,
  useGetSaleInvoiceChargesQuery,
  useGetPendingInvoicesListQuery,
  useGetPrePaymentsQuery,
  useLazyGetPrePaymentSelectionQuery,
  useLazyGetPrePaymentByIdQuery,
  useGetPrePaymentsByPOIdQuery,
  useGetSupplierCreditNoteQuery,
  useGetSupplierCreditNotesQuery,
  useLazyGetSupplierCreditNotesSelectionQuery,
  useSubmitSupplierCreditNoteToXeroMutation,
  useUpdateSupplierCreditNoteMutation,
  useUpdateSupplierCreditNoteStatusMutation,
  usePublishAllInvoicesManualMutation,
  usePublishAllInvoicesMutation,
  useRemoveInvoiceMutation,
  useSubmitBillMutation,
  useUpdateBillMutation,
  useUpdateBillStatusMutation,
} = invoiceApiSlice;
